import { ChevronDownIcon, ChevronRightIcon } from 'lucide-react';
import { ColumnDef } from '@tanstack/react-table';
import { StudyData, Schema, ExpandRowProps } from '@/types/types';
import { z } from 'zod';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../../components/ui/tooltip';
import { CopyButton } from '../../../components/ui/copy-button';
import { DateRange } from 'react-day-picker';
import { DownloadButton } from './downloadButton';
import { NormalizeButton } from './normalizeButton';
import { ViewerButton } from './viewerButton';

/** Calendar date in local TZ (avoid UTC shift from toISOString on date-only filters). */
function toLocalYyyyMmDd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function uploadDateToYyyyMmDd(raw: string | null | undefined): string | null {
  if (raw == null || typeof raw !== 'string') return null;
  const s = raw.trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const t = new Date(s);
  if (Number.isNaN(t.getTime())) return null;
  return toLocalYyyyMmDd(t);
}

function cellValueAsString(value: unknown): string {
  return typeof value === 'string' ? value : String(value ?? '');
}

/** Ascending compare with empty/null values last (TanStack inverts for desc). */
function compareOptionalStringAsc(a: unknown, b: unknown): number {
  const aNorm = cellValueAsString(a).trim().toLowerCase();
  const bNorm = cellValueAsString(b).trim().toLowerCase();
  const aEmpty = aNorm === '';
  const bEmpty = bNorm === '';
  if (aEmpty && bEmpty) return 0;
  if (aEmpty) return 1;
  if (bEmpty) return -1;
  return aNorm.localeCompare(bNorm);
}

export const baseColumns: ColumnDef<z.infer<typeof Schema>>[] = [
  {
    id: 'slNo',
    cell: () => null,
    enableSorting: false,
    meta: {
      isDefaultVisible: false,
    }
  },
  {
    accessorKey: 'StudyInstanceUID',
    header: 'Study Instance UID',
    cell: ({ row }) => (
      <div className="flex items-center gap-2 text-left font-bold">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="cursor-pointer max-w-60 overflow-hidden truncate block">
                {row.original.StudyInstanceUID || 'N/A'}
              </span>
            </TooltipTrigger>
            <TooltipContent>
              <span>{row.original.StudyInstanceUID || 'N/A'}</span>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
        {row.original.StudyInstanceUID && (
          <CopyButton 
            value={row.original.StudyInstanceUID}
            label="Copy Study Instance UID"
          />
        )}
      </div>
    ),
    enableSorting: true,
    meta: {
      isDefaultVisible: true,
    }
  },
  {
    accessorKey: 'StudyDescription',
    header: 'Study Description',
    cell: ({ row }) => (
      <div className="flex">
        {row.original.StudyDescription || 'No description'}
      </div>
    ),
    enableSorting: true,
    meta: {
      isDefaultVisible: false,
    },
    filterFn: (row, columnId, filterValue) => {
      const rawValue = row.getValue(columnId);
      const value =
        typeof rawValue === 'string' ? rawValue : String(rawValue ?? '');
      return value.toLowerCase().includes((filterValue ?? '').toLowerCase());
    },
  },
  {
    accessorKey: 'StudyDate',
    header: 'Study Date',
    filterFn: (row, columnId, filterValue: DateRange | undefined) => {
      if (!filterValue || !filterValue.from || !filterValue.to) return true;
      const rowDate = new Date(row.getValue(columnId));
      const from = new Date(filterValue.from);
      const to = new Date(filterValue.to);
      to.setHours(23, 59, 59, 999);
      return rowDate >= from && rowDate <= to;
    },
    cell: ({ row }) => (
      <div className="flex px-2">{row.original.StudyDate || 'N/A'}</div>
    ),
    enableSorting: true,
    meta: {
      isDefaultVisible: true,
    }
  },
  {
    accessorKey: 'UploadDate',
    header: 'Upload Date',
    filterFn: (row, columnId, filterValue: Date | undefined) => {
      if (!filterValue) return true;
      const raw = row.getValue(columnId) as string | null | undefined;
      const rowYmd = uploadDateToYyyyMmDd(raw);
      if (!rowYmd) return false;
      return rowYmd === toLocalYyyyMmDd(filterValue);
    },
    sortingFn: (rowA, rowB, columnId) => {
      const aRaw = rowA.getValue(columnId) as string | null | undefined;
      const bRaw = rowB.getValue(columnId) as string | null | undefined;
      const a = aRaw ? new Date(aRaw).getTime() : 0;
      const b = bRaw ? new Date(bRaw).getTime() : 0;
      return a - b;
    },
    cell: ({ row }) => (
      <div className="flex px-2">{row.original.UploadDate || 'N/A'}</div>
    ),
    enableSorting: true,
    meta: {
      isDefaultVisible: false,
    },
  },
  {
    accessorKey: 'Modality',
    header: 'Modality',
    filterFn: (row, columnId, filterValue: string[] | undefined) => {
      if (!filterValue || filterValue.length === 0) return true;
      const rowValue = row.getValue(columnId) as string;
      return filterValue.includes(rowValue);
    },
    cell: ({ row }) => (
      <div className="flex px-8 text-muted-foreground">
        {row.original.Modality}
      </div>
    ),
    enableSorting: true,
    meta: {
      isDefaultVisible: false,
    }
  },
  {
    accessorKey: 'InstanceCount',
    header: 'No of Instances',
    filterFn: (row, columnId, filterValue: [string, string] | undefined) => {
      if (!filterValue) return true;
      const [min, max] = filterValue;
      const rowValue = Number(row.getValue(columnId));

      if (min !== '' && rowValue < Number(min)) return false;
      if (max !== '' && rowValue > Number(max)) return false;
      return true;
    },
    cell: ({ row }) => (
      <div className="flex w-auto px-8">{row.original.InstanceCount}</div>
    ),
    enableSorting: true,
    meta: {
      isDefaultVisible: false,
    }
  },
  {
    accessorKey: 'Institution',
    header: 'Institution',
    filterFn: (row, columnId, filterValue: string | undefined) => {
      if (!filterValue || filterValue.trim() === '') return true;
      const rowValue = row.getValue(columnId) as string;
      return rowValue?.toLowerCase().includes(filterValue.toLowerCase());
    },
    cell: ({ row }) => (
      <div className="flex px-8 text-muted-foreground">
        {row.original.Institution || 'N/A'}
      </div>
    ),
    enableSorting: true,
    meta: {
      isDefaultVisible: false,
    },
  },
  {
    accessorKey: 'DeviceSerialID',
    header: 'DeviceSerialID',
    filterFn: (row, columnId, filterValue: string[] | undefined) => {
      if (!filterValue || filterValue.length === 0) return true;
      const rowValue = row.getValue(columnId) as string;
      return filterValue.includes(rowValue);
    },
    cell: ({ row }) => (
      <div className="flex px-8 text-muted-foreground">
        {row.original.DeviceSerialID || 'N/A'}
      </div>
    ),
    enableSorting: true,
    meta: {
      isDefaultVisible: false,
    },
  },
  {
  accessorKey: 'StudyCondition',
  header: 'Stratagen ID',
  filterFn: (row, columnId, filterValue: string | undefined) => {
    if (!filterValue || filterValue.trim() === '') return true;
    const rowValue = row.getValue(columnId) as string;
    return rowValue?.toLowerCase().includes(filterValue.toLowerCase());
  },
  cell: ({ row }) => (
    <div className="flex px-8 text-muted-foreground">
      {row.original.StudyCondition || 'N/A'}
    </div>
  ),
  enableSorting: true,
  meta: {
    isDefaultVisible: true,
  },
  },
  {
    accessorKey: 'HumanSubjectId',
    header: 'HumanSubjectId',
    filterFn: (row, columnId, filterValue: string | undefined) => {
      if (!filterValue || filterValue.trim() === '') return true;
      const rowValue = cellValueAsString(row.getValue(columnId)).trim();
      if (!rowValue) return false;
      return rowValue.toLowerCase().includes(filterValue.toLowerCase());
    },
    sortingFn: (rowA, rowB, columnId) =>
      compareOptionalStringAsc(
        rowA.getValue(columnId),
        rowB.getValue(columnId)
      ),
    cell: ({ row }) => (
      <div className="flex px-8 text-muted-foreground">
        {row.original.HumanSubjectId || 'N/A'}
      </div>
    ),
    enableSorting: true,
    meta: {
      isDefaultVisible: true,
    },
  },
  {
    id: 'download',
    cell: ({ row }) => (
      <div className="flex w-auto">
        <DownloadButton
          studyInstanceUID={row.original.StudyInstanceUID}
          studyDescription={row.original.StudyDescription || 'no-description'}
          studyDate={row.original.StudyDate || 'no-study-date'}
        />
      </div>
    ),
    meta: {
      isDefaultVisible: true,
    },
  },
  {
    id: 'viewer',
    header: () => null,
    cell: ({ row }) => (
      <div className="flex w-auto">
        <ViewerButton studyInstanceUID={row.original.StudyInstanceUID} />
      </div>
    ),
    meta: {
      isDefaultVisible: true,
    },
  },
  {
    id: 'normalize',
    header: () => null,
    cell: ({ row }) => (
      <div className="flex w-auto">
        <NormalizeButton
          studyInstanceUID={row.original.StudyInstanceUID}
          studyDescription={row.original.StudyDescription || 'no-description'} 
        />
      </div>
    ),
    meta: {
      isDefaultVisible: true,
    },
  },
];

export const ExpandRowCell = ({
  isExpanded,
  onToggle,
  isLoading,
}: ExpandRowProps) => (
  <button
    onClick={onToggle}
    disabled={isLoading}
    aria-label={isExpanded ? 'Collapse row' : 'Expand row'}
    className="flex items-center focus:outline-none"
  >
    {isExpanded ? (
      <ChevronDownIcon className="text-muted-foreground" />
    ) : (
      <ChevronRightIcon className="text-muted-foreground" />
    )}
  </button>
);

export const createExpandableColumn = (expandHandlers: {
  isExpanded: (rowId: string) => boolean;
  isLoading: (rowId: string) => boolean;
  toggleRow: (rowId: string, studyInstanceUID: string) => Promise<void>;
}): ColumnDef<StudyData> => ({
  id: 'chevron',
  header: () => null,
  cell: ({ row }) => {
    const isExpanded = expandHandlers.isExpanded(row.id);
    const isLoading = expandHandlers.isLoading(row.id);

    return (
      <ExpandRowCell
        isExpanded={isExpanded}
        isLoading={isLoading}
        onToggle={() =>
          expandHandlers.toggleRow(row.id, row.original.StudyInstanceUID)
        }
      />
    );
  },
});
